import { createContext, useContext, useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabaseClient';
import { fetchUserWatchlist, addToUserWatchlist, removeFromUserWatchlist } from '../services/api';

const WatchlistContext = createContext();

export function WatchlistProvider({ children }) {
  const [watchlist, setWatchlist] = useState(() => {
    try {
      const saved = localStorage.getItem('hqs-watchlist');
      const parsed = saved ? JSON.parse(saved) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });

  const dbSyncedRef = useRef(false);

  // ── DB sync on mount / auth state change ──────────────────────────────
  useEffect(() => {
    let cancelled = false;

    async function syncFromDb() {
      try {
        const session = (await supabase?.auth?.getSession?.())?.data?.session;
        if (!session) return;

        const { data, error } = await fetchUserWatchlist();
        if (cancelled) return;
        if (error) {
          console.warn('[WatchlistContext] DB-Sync fehlgeschlagen:', error);
          return;
        }
        const dbSymbols = Array.isArray(data?.symbols) ? data.symbols : [];
        if (dbSymbols.length > 0) {
          setWatchlist(dbSymbols);
          localStorage.setItem('hqs-watchlist', JSON.stringify(dbSymbols));
        } else {
          // DB empty → migrate localStorage → DB (fire-and-forget)
          const local = (() => {
            try { return JSON.parse(localStorage.getItem('hqs-watchlist') || '[]'); } catch { return []; }
          })();
          if (Array.isArray(local) && local.length > 0) {
            for (const sym of local) {
              addToUserWatchlist(sym).catch(() => {});
            }
          }
        }
        dbSyncedRef.current = true;
      } catch (e) {
        console.warn('[WatchlistContext] Sync-Fehler:', e);
      }
    }

    syncFromDb();

    const { data: authListener } = supabase?.auth?.onAuthStateChange?.((event) => {
      if (event === 'SIGNED_IN') {
        dbSyncedRef.current = false;
        syncFromDb();
      } else if (event === 'SIGNED_OUT') {
        dbSyncedRef.current = false;
      }
    }) ?? { data: null };

    return () => {
      cancelled = true;
      authListener?.subscription?.unsubscribe?.();
    };
  }, []);

  // ── Persist to localStorage on every change ────────────────────────────
  useEffect(() => {
    localStorage.setItem('hqs-watchlist', JSON.stringify(watchlist));
  }, [watchlist]);

  const addToWatchlist = (symbol) => {
    const upper = String(symbol || '').toUpperCase().trim();
    if (!upper || watchlist.includes(upper)) return;
    setWatchlist(prev => [...prev, upper]);
    addToUserWatchlist(upper).catch((e) =>
      console.warn('[WatchlistContext] DB-Hinzufügen fehlgeschlagen:', e)
    );
  };

  const removeFromWatchlist = (symbol) => {
    const upper = String(symbol || '').toUpperCase().trim();
    setWatchlist(prev => prev.filter(s => s !== upper));
    removeFromUserWatchlist(upper).catch((e) =>
      console.warn('[WatchlistContext] DB-Entfernen fehlgeschlagen:', e)
    );
  };

  const toggleWatchlist = (symbol) => {
    if (watchlist.includes(String(symbol || '').toUpperCase().trim())) {
      removeFromWatchlist(symbol);
    } else {
      addToWatchlist(symbol);
    }
  };

  const isInWatchlist = (symbol) => {
    return watchlist.includes(String(symbol || '').toUpperCase().trim());
  };

  return (
    <WatchlistContext.Provider value={{ 
      watchlist, 
      addToWatchlist, 
      removeFromWatchlist, 
      toggleWatchlist,
      isInWatchlist 
    }}>
      {children}
    </WatchlistContext.Provider>
  );
}

export function useWatchlist() {
  const context = useContext(WatchlistContext);
  if (!context) {
    throw new Error('useWatchlist must be used within WatchlistProvider');
  }
  return context;
}
